import { useEffect, useRef } from 'react';

/**
 * Reveal an element once it scrolls into view.
 *
 * Attach the returned ref to any node carrying the `.reveal` class; the
 * observer adds `.is-visible` and then stops watching, so nothing re-animates
 * as the visitor scrolls back up.
 *
 * @param {object} options
 * @param {number} options.threshold  Fraction visible before revealing.
 * @param {string} options.rootMargin Fires slightly early so the animation has
 *                                    finished by the time the element is read.
 */
export function useReveal({ threshold = 0.12, rootMargin = '0px 0px -60px 0px' } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Without IntersectionObserver, show the content rather than hide it.
    if (typeof IntersectionObserver === 'undefined') {
      node.classList.add('is-visible');
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
}

/**
 * Reveal a group of children in sequence — used for card grids so they cascade
 * instead of all appearing at once.
 */
export function useRevealGroup({ stagger = 70, threshold = 0.08 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return undefined;

    const children = Array.from(container.children);

    if (typeof IntersectionObserver === 'undefined') {
      children.forEach((child) => child.classList.add('is-visible'));
      return undefined;
    }

    children.forEach((child) => child.classList.add('reveal'));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = children.indexOf(entry.target);
          entry.target.style.transitionDelay = `${Math.max(0, index) * stagger}ms`;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );

    children.forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [stagger, threshold]);

  return ref;
}
